import React from "react";
import { Link } from "@tanstack/react-router";
import { type LucideIcon, TrendingUp, TrendingDown, Minus } from "lucide-react";
import { cn, formatNumber, formatCurrency } from "@/lib/utils";
import { Skeleton } from "@/components/ui/skeleton";

export type KpiColorVariant =
  | "default"
  | "emerald"
  | "blue"
  | "amber"
  | "violet"
  | "rose"
  | "teal"
  | "indigo"
  | "slate";

export type KpiFormatType = "currency" | "currencyShort" | "number" | "percent" | "raw";

export const formatShortVnd = (n: number | string) => {
  const num = typeof n === "string" ? parseFloat(n) : n;
  if (isNaN(num)) return "0đ";
  if (Math.abs(num) >= 1_000_000_000)
    return (num / 1_000_000_000).toFixed(2).replace(/\.?0+$/, "") + " tỷ";
  if (Math.abs(num) >= 1_000_000)
    return (num / 1_000_000).toFixed(1).replace(/\.?0+$/, "") + " tr";
  if (Math.abs(num) >= 1_000) return (num / 1_000).toFixed(0) + "K";
  return new Intl.NumberFormat("vi-VN").format(num) + "đ";
};

export interface KpiStatCardProps {
  title: string;
  value: number | string;
  prevValue?: number | string;
  deltaPct?: number | null;
  diff?: number | null;
  unit?: string;
  formatType?: KpiFormatType;
  direction?: "positive" | "inverse"; // positive: up is good, inverse: up is bad (scrap, debt, stopped machine)
  trendLabel?: string; // e.g. "vs tháng trước" or custom text
  icon?: LucideIcon | React.ComponentType<{ className?: string }>;
  colorVariant?: KpiColorVariant;
  badgeText?: string; // custom override badge text
  subText?: React.ReactNode;
  onClick?: () => void;
  href?: string;
  isLoading?: boolean;
  className?: string;
  testId?: string;
}

const COLOR_MAP: Record<
  KpiColorVariant,
  {
    iconBg: string;
    iconColor: string;
    borderTop?: string;
  }
> = {
  default: {
    iconBg: "bg-primary/10",
    iconColor: "text-primary",
    borderTop: "border-t-primary",
  },
  emerald: {
    iconBg: "bg-emerald-500/10",
    iconColor: "text-emerald-600",
    borderTop: "border-t-emerald-500",
  },
  blue: {
    iconBg: "bg-blue-500/10",
    iconColor: "text-blue-600",
    borderTop: "border-t-blue-500",
  },
  amber: {
    iconBg: "bg-amber-500/10",
    iconColor: "text-amber-600",
    borderTop: "border-t-amber-500",
  },
  violet: {
    iconBg: "bg-violet-500/10",
    iconColor: "text-violet-600",
    borderTop: "border-t-violet-500",
  },
  rose: {
    iconBg: "bg-rose-500/10",
    iconColor: "text-rose-600",
    borderTop: "border-t-rose-500",
  },
  teal: {
    iconBg: "bg-teal-500/10",
    iconColor: "text-teal-600",
    borderTop: "border-t-teal-500",
  },
  indigo: {
    iconBg: "bg-indigo-500/10",
    iconColor: "text-indigo-600",
    borderTop: "border-t-indigo-500",
  },
  slate: {
    iconBg: "bg-slate-500/10",
    iconColor: "text-slate-600",
    borderTop: "border-t-slate-500",
  },
};

export function KpiStatCard({
  title,
  value,
  prevValue,
  deltaPct,
  diff,
  unit,
  formatType = "number",
  direction = "positive",
  trendLabel,
  icon: Icon,
  colorVariant = "default",
  badgeText,
  subText,
  onClick,
  href,
  isLoading = false,
  className,
  testId,
}: KpiStatCardProps) {
  if (isLoading) {
    return (
      <div
        data-testid={testId}
        className={cn(
          "bg-card rounded-xl border border-border p-3.5 shadow-2xs",
          className
        )}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Skeleton className="w-7 h-7 rounded-lg" />
            <Skeleton className="w-20 h-4" />
          </div>
          <Skeleton className="w-12 h-4 rounded" />
        </div>
        <div className="mt-3">
          <Skeleton className="w-28 h-7" />
        </div>
        <div className="mt-2">
          <Skeleton className="w-36 h-3.5" />
        </div>
      </div>
    );
  }

  // Format main value
  let formattedValue = String(value);
  const numVal = typeof value === "number" ? value : parseFloat(String(value));
  if (!isNaN(numVal)) {
    switch (formatType) {
      case "currency":
        formattedValue = formatCurrency(numVal);
        break;
      case "currencyShort":
        formattedValue = formatShortVnd(numVal);
        break;
      case "number":
        formattedValue = formatNumber(numVal);
        break;
      case "percent":
        formattedValue = `${numVal}%`;
        break;
      case "raw":
      default:
        formattedValue = String(value);
        break;
    }
  }

  // Format previous value text
  let prevFormatted: string | null = null;
  if (prevValue !== undefined && prevValue !== null) {
    const numPrev = typeof prevValue === "number" ? prevValue : parseFloat(String(prevValue));
    if (!isNaN(numPrev)) {
      switch (formatType) {
        case "currency":
          prevFormatted = formatCurrency(numPrev);
          break;
        case "currencyShort":
          prevFormatted = formatShortVnd(numPrev);
          break;
        case "number":
          prevFormatted = formatNumber(numPrev);
          break;
        case "percent":
          prevFormatted = `${numPrev}%`;
          break;
        default:
          prevFormatted = String(prevValue);
          break;
      }
    } else {
      prevFormatted = String(prevValue);
    }
  }

  // Delta calculation & Styling
  const hasDelta = deltaPct !== undefined && deltaPct !== null && !isNaN(deltaPct);
  const isZero = hasDelta && deltaPct === 0;
  const isUp = hasDelta && deltaPct > 0;
  
  // Evaluation of "good" vs "bad"
  // For positive direction (revenue, orders): Up is good, Down is bad
  // For inverse direction (scrap rate, defect, overdue debt, downtime): Up is bad, Down is good
  const isGood = direction === "positive" ? isUp : !isUp && !isZero;
  const isBad = direction === "positive" ? !isUp && !isZero : isUp;

  let pillClasses = "bg-muted text-muted-foreground border-border";
  if (hasDelta && !isZero) {
    if (isGood) {
      pillClasses =
        "bg-emerald-500/10 text-emerald-700 border-emerald-500/20";
    } else if (isBad) {
      pillClasses =
        "bg-rose-500/10 text-rose-700 border-rose-500/20";
    }
  }

  const colors = COLOR_MAP[colorVariant] || COLOR_MAP.default;

  const cardContent = (
    <div
      data-testid={testId}
      className={cn(
        "bg-card rounded-xl border border-border p-3.5 shadow-2xs transition-all duration-150 select-none",
        (onClick || href) && "cursor-pointer hover:border-primary/50 hover:shadow-xs active:scale-[0.99]",
        className
      )}
      onClick={onClick}
    >
      {/* Top Row: Icon + Title + Pill Badge */}
      <div className="flex items-center justify-between gap-1.5">
        <div className="flex items-center gap-2 min-w-0">
          {Icon && (
            <div
              className={cn(
                "p-1.5 rounded-lg shrink-0 flex items-center justify-center",
                colors.iconBg,
                colors.iconColor
              )}
            >
              <Icon className="w-4 h-4" />
            </div>
          )}
          {/* Tiêu đề xuống dòng thay vì cắt "…" (02/11/2026: thẻ hẹp cắt "Tổng giao dịch 7 ngày") */}
          <span className="text-xs font-bold text-foreground/90 uppercase tracking-wide leading-tight break-words min-w-0">
            {title}
          </span>
        </div>

        {/* Delta Badge */}
        {hasDelta ? (
          <span
            className={cn(
              "inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-[10px] font-bold font-mono border shrink-0",
              pillClasses
            )}
            title={trendLabel || (prevFormatted ? `Kỳ trước: ${prevFormatted}` : undefined)}
          >
            {isZero ? (
              <Minus className="w-2.5 h-2.5" />
            ) : isUp ? (
              <TrendingUp className="w-2.5 h-2.5" />
            ) : (
              <TrendingDown className="w-2.5 h-2.5" />
            )}
            <span>
              {isUp ? "+" : ""}
              {deltaPct.toFixed(1)}%
            </span>
          </span>
        ) : badgeText ? (
          <span className="inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold font-mono border bg-muted text-muted-foreground border-border shrink-0">
            {badgeText}
          </span>
        ) : null}
      </div>

      {/* Middle Row: Big Monospace Value */}
      <div className="mt-2.5 flex items-baseline gap-1">
        <span className="text-2xl font-black text-foreground font-mono tracking-tight">
          {formattedValue}
        </span>
        {unit && (
          <span className="text-xs font-semibold text-muted-foreground ml-0.5">
            {unit}
          </span>
        )}
      </div>

      {/* Bottom Row: Context Comparison / Trend Label */}
      <div className="mt-1 flex items-center justify-between text-[11px] text-muted-foreground min-h-[16px]">
        {trendLabel ? (
          <span className="truncate">{trendLabel}</span>
        ) : prevFormatted ? (
          <span className="truncate">
            Kỳ trước: <strong className="font-mono text-foreground/80">{prevFormatted}</strong>
          </span>
        ) : subText ? (
          <div className="truncate">{subText}</div>
        ) : (
          <span className="opacity-0">—</span>
        )}

        {/* Optional Diff text */}
        {diff !== undefined && diff !== null && diff !== 0 && !trendLabel && (
          <span className="font-mono text-[10px] ml-1 shrink-0 opacity-70">
            ({diff > 0 ? "+" : ""}
            {formatType === "currency" || formatType === "currencyShort"
              ? formatShortVnd(diff)
              : formatNumber(diff)}
            )
          </span>
        )}
      </div>
    </div>
  );

  if (href) {
    return (
      <Link to={href as any} className="block group">
        {cardContent}
      </Link>
    );
  }

  return cardContent;
}
